import * as React from "react";
import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import { AclContext } from "../../../../../contexts/AclContext";
import { fxs } from "../../../../../utils/firestore";
import { LeaWorkflow } from "./leaWorkflow";

type LeaAction = "get" | "takeover" | "end_test";

interface LeaWorkflowState {
    workflow: LeaWorkflow | null;
    loading: boolean;
    pending: boolean;
    error: boolean;
    /** The client was checked and has never been handed to Lea AI. */
    notStarted: boolean;
    refresh: () => Promise<boolean>;
    runAction: (action: "takeover" | "end_test") => Promise<boolean>;
}

const AI_DRAFT_ENABLED = process.env.REACT_APP_AI_INTAKE_DRAFT_ENABLED === "true";

const LeaWorkflowContext = React.createContext<LeaWorkflowState | null>(null);

export function LeaWorkflowProvider({
    clientId,
    children,
}: {
    clientId: string;
    children: React.ReactNode;
}) {
    const userAcl = React.useContext(AclContext)?.authUser?.acl;
    const enabled =
        AI_DRAFT_ENABLED && Boolean(userAcl?.intake || userAcl?.super_admin);
    const [workflow, setWorkflow] = React.useState<LeaWorkflow | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [pending, setPending] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const pendingRef = React.useRef(false);

    const request = React.useCallback(
        async (action: LeaAction) => {
            const call = httpsCallable<
                { client_id: string; event_id: string; action: LeaAction },
                LeaWorkflow
            >(fxs, "httpIntakeEngagementDraft");
            const result = await call({
                client_id: clientId,
                event_id: uuidv4(),
                action,
            });
            // Clients that were never handed to Lea AI have no test number.
            return result.data.test_recipient ? result.data : null;
        },
        [clientId]
    );

    React.useEffect(() => {
        if (!enabled) return;
        let active = true;
        setWorkflow(null);
        setError(false);
        setLoaded(false);
        setLoading(true);
        request("get")
            .then((result) => {
                if (!active) return;
                setWorkflow(result);
                setLoaded(true);
            })
            .catch(() => {
                if (active) setError(true);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [request, enabled]);

    const run = React.useCallback(
        async (action: LeaAction) => {
            if (!enabled || pendingRef.current) return false;
            pendingRef.current = true;
            setPending(true);
            setError(false);
            try {
                setWorkflow(await request(action));
                setLoaded(true);
                return true;
            } catch {
                setError(true);
                return false;
            } finally {
                pendingRef.current = false;
                setPending(false);
            }
        },
        [enabled, request]
    );

    const value = React.useMemo<LeaWorkflowState>(
        () => ({
            workflow: enabled ? workflow : null,
            loading,
            pending,
            error: enabled && error,
            notStarted: enabled && loaded && !workflow,
            refresh: () => run("get"),
            runAction: run,
        }),
        [enabled, workflow, loading, pending, error, loaded, run]
    );

    return (
        <LeaWorkflowContext.Provider value={value}>
            {children}
        </LeaWorkflowContext.Provider>
    );
}

export function useLeaWorkflow(): LeaWorkflowState | null {
    return React.useContext(LeaWorkflowContext);
}
